import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';
import { createExpense } from './expenseService';
import { logActivity } from './activityLogService';

const ADJUSTMENTS_COLLECTION = 'stockAdjustments';

// Returns today's date in local time as YYYY-MM-DD (avoids UTC timezone shift)
const localDateString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Process a stock adjustment for a single item.
 * - Shortage: deducts from FIFO batches, creates a stock loss expense
 * - Surplus: adds to the most recent active batch, no financial entry
 */
export const processStockAdjustment = async (item, physicalCount, reason, currentUser) => {
  try {
    const itemRef = doc(db, 'inventory', item.id);
    const itemSnap = await getDoc(itemRef);
    if (!itemSnap.exists()) return { success: false, error: 'Item not found' };

    const itemData = itemSnap.data();
    const systemCount = itemData.stock || 0;
    const variance = physicalCount - systemCount; // negative = shortage, positive = surplus

    if (variance === 0) return { success: true, variance: 0, message: 'No adjustment needed' };

    // ── ML-tracked decant items — no batch logic, update stock directly ──
    if (itemData.trackInML) {
      const lossValue = variance < 0
        ? Math.abs(variance) * (itemData.costPrice || 0)
        : 0;

      await updateDoc(itemRef, {
        stock: physicalCount,
        updatedAt: serverTimestamp(),
      });

      let expenseId = null;
      if (variance < 0 && lossValue > 0) {
        const expenseData = {
          title: `Stock Adjustment — ${item.name}`,
          categoryId: 'stock-adjustment',
          categoryName: 'Stock Adjustment',
          categoryType: 'loss',
          affectsInventory: false,
          date: localDateString(),
          amount: lossValue,
          paymentMethod: 'N/A',
          referenceNo: '',
          notes: reason || 'Physical count adjustment',
          recordedBy: currentUser?.uid || '',
          recordedByName: currentUser?.email || '',
        };
        const expResult = await createExpense(expenseData, []);
        if (expResult.success) expenseId = expResult.id;
      }

      await logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        userName: currentUser?.displayName || currentUser?.email,
        action: 'stock_adjustment',
        entityType: 'inventory',
        entityId: item.id,
        entityName: item.name,
        details: {
          systemCount,
          physicalCount,
          variance,
          lossValue,
          reason,
          trackInML: true,
        },
      });

      return {
        success: true,
        variance,
        lossValue,
        newStock: physicalCount,
        expenseId,
      };
    }

    let batches = [...(itemData.batches || [])].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    let lossValue = 0;

    if (variance < 0) {
      // ── SHORTAGE: deduct from oldest batches first (FIFO) ──
      let remaining = Math.abs(variance);
      for (const batch of batches) {
        if (remaining <= 0) break;
        const deduct = Math.min(batch.quantityRemaining, remaining);
        lossValue += deduct * (batch.costPrice || 0);
        batch.quantityRemaining -= deduct;
        remaining -= deduct;
      }
    } else {
      // ── SURPLUS: create a new adjustment batch with average cost ──
      // We don't know which real batch these units belong to, so we use
      // the current average cost as the cost price — standard accounting treatment.
      // Retail/wholesale prices are copied from the newest active batch so billing works correctly.
      const avgCost = itemData.averageCost || itemData.costPrice || 0;
      const activeBatches = batches.filter(b => (b.quantityRemaining || 0) > 0);
      const newestBatch = activeBatches.length > 0
        ? activeBatches[activeBatches.length - 1]
        : batches[batches.length - 1];
      const adjustmentBatch = {
        batchId: `adj-${Date.now()}`,
        purchaseInvoiceId: 'adjustment',
        purchaseInvoiceNumber: 'ADJ',
        transferNumber: 'ADJ',
        purchaseDate: localDateString(),
        costPrice: avgCost,
        retailPrice: newestBatch?.retailPrice || 0,
        wholesalePrice: newestBatch?.wholesalePrice || 0,
        discount: 0,
        quantityPurchased: variance,
        quantityRemaining: variance,
        supplier: 'Stock Adjustment',
        source: 'adjustment',
        adjustmentReason: reason || 'Physical count surplus',
        createdAt: new Date().toISOString(),
      };
      batches.push(adjustmentBatch);
      // No financial entry for surplus — found stock has no cash impact
    }

    // Recalculate totals
    const newStock = batches.reduce((s, b) => s + (b.quantityRemaining || 0), 0);
    const totalCost = batches.reduce((s, b) => s + (b.costPrice || 0) * (b.quantityRemaining || 0), 0);
    const averageCost = newStock > 0 ? totalCost / newStock : itemData.averageCost || 0;

    await updateDoc(itemRef, {
      batches,
      stock: newStock,
      averageCost,
      updatedAt: serverTimestamp(),
    });

    // For shortages — create a stock loss expense automatically
    let expenseId = null;
    if (variance < 0 && lossValue > 0) {
      const expenseData = {
        title: `Stock Adjustment — ${item.name}`,
        categoryId: 'stock-adjustment',   // synthetic ID — no real category doc needed
        categoryName: 'Stock Adjustment',
        categoryType: 'loss',
        affectsInventory: false,           // stock already adjusted above
        date: localDateString(),
        amount: lossValue,                 // set amount so grandTotal calculates correctly
        paymentMethod: 'N/A',
        referenceNo: '',
        notes: reason || 'Quarterly physical inventory count',
        recordedBy: currentUser?.uid || '',
        recordedByName: currentUser?.email || '',
      };
      const expResult = await createExpense(expenseData, []);
      if (expResult.success) expenseId = expResult.id;
    }

    // Save adjustment record
    const adjustmentRef = await addDoc(collection(db, ADJUSTMENTS_COLLECTION), {
      itemId: item.id,
      itemName: item.name,
      category: item.category || '',
      systemCount,
      physicalCount,
      variance,
      lossValue: variance < 0 ? lossValue : 0,
      reason: reason || '',
      expenseId,
      adjustedBy: currentUser?.uid || '',
      adjustedByName: currentUser?.email || '',
      date: localDateString(),
      createdAt: serverTimestamp(),
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'stock_adjustment',
      entityType: 'inventory',
      entityId: item.id,
      entityName: item.name,
      details: { systemCount, physicalCount, variance, lossValue, reason },
    });

    return {
      success: true,
      adjustmentId: adjustmentRef.id,
      variance,
      lossValue,
      newStock,
    };
  } catch (error) {
    console.error('Error processing stock adjustment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Process a full stocktake (multiple items at once)
 */
export const processStocktake = async (adjustments, reason, currentUser) => {
  const results = [];
  for (const adj of adjustments) {
    if (adj.physicalCount === adj.systemCount) continue; // skip unchanged
    const result = await processStockAdjustment(
      { id: adj.itemId, name: adj.itemName, category: adj.category },
      adj.physicalCount,
      reason,
      currentUser
    );
    results.push({ ...adj, ...result });
  }
  return { success: true, results };
};

/**
 * Get adjustment history
 */
export const getAdjustmentHistory = async () => {
  try {
    const q = query(collection(db, ADJUSTMENTS_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return {
      success: true,
      data: snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
    };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Reverse a stock adjustment
 * - Restores inventory to the original system count
 * - Deletes the associated expense record if it exists
 * - Marks the adjustment as reversed
 */
export const reverseStockAdjustment = async (adjustmentId, currentUser) => {
  try {
    const adjustmentRef = doc(db, ADJUSTMENTS_COLLECTION, adjustmentId);
    const adjustmentSnap = await getDoc(adjustmentRef);
    
    if (!adjustmentSnap.exists()) {
      return { success: false, error: 'Adjustment record not found' };
    }

    const adjustment = adjustmentSnap.data();
    
    // Check if already reversed
    if (adjustment.reversed) {
      return { success: false, error: 'This adjustment has already been reversed' };
    }

    const { itemId, itemName, systemCount, physicalCount, variance, expenseId } = adjustment;

    // Get current inventory item
    const itemRef = doc(db, 'inventory', itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (!itemSnap.exists()) {
      return { success: false, error: 'Inventory item not found' };
    }

    const itemData = itemSnap.data();
    let batches = [...(itemData.batches || [])].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    // Reverse the adjustment
    if (variance < 0) {
      // Original was a SHORTAGE — restore stock by adding to newest batch or creating new batch
      const activeBatches = batches.filter(b => (b.quantityRemaining || 0) > 0);
      const newestBatch = activeBatches.length > 0
        ? activeBatches[activeBatches.length - 1]
        : batches[batches.length - 1];
      
      // Create a reversal batch
      const avgCost = itemData.averageCost || itemData.costPrice || 0;
      const reversalBatch = {
        batchId: `rev-${Date.now()}`,
        purchaseInvoiceId: 'reversal',
        purchaseInvoiceNumber: 'REV',
        transferNumber: 'REV',
        purchaseDate: localDateString(),
        costPrice: avgCost,
        retailPrice: newestBatch?.retailPrice || 0,
        wholesalePrice: newestBatch?.wholesalePrice || 0,
        discount: 0,
        quantityPurchased: Math.abs(variance),
        quantityRemaining: Math.abs(variance),
        supplier: 'Adjustment Reversal',
        source: 'reversal',
        adjustmentReason: `Reversal of adjustment: ${adjustment.reason || 'N/A'}`,
        createdAt: new Date().toISOString(),
      };
      batches.push(reversalBatch);
    } else {
      // Original was a SURPLUS — remove the added stock from newest batches (LIFO for reversal)
      let remaining = variance;
      // Reverse order for LIFO removal
      for (let i = batches.length - 1; i >= 0 && remaining > 0; i--) {
        const batch = batches[i];
        const deduct = Math.min(batch.quantityRemaining, remaining);
        batch.quantityRemaining -= deduct;
        remaining -= deduct;
      }
    }

    // Recalculate totals
    const newStock = batches.reduce((s, b) => s + (b.quantityRemaining || 0), 0);
    const totalCost = batches.reduce((s, b) => s + (b.costPrice || 0) * (b.quantityRemaining || 0), 0);
    const averageCost = newStock > 0 ? totalCost / newStock : itemData.averageCost || 0;

    // Update inventory
    await updateDoc(itemRef, {
      batches,
      stock: newStock,
      averageCost,
      updatedAt: serverTimestamp(),
    });

    // Delete associated expense if it exists
    if (expenseId) {
      try {
        const { deleteExpense } = await import('./expenseService');
        await deleteExpense(expenseId);
      } catch (error) {
        console.error('Error deleting associated expense:', error);
        // Continue even if expense deletion fails
      }
    }

    // Mark adjustment as reversed
    await updateDoc(adjustmentRef, {
      reversed: true,
      reversedAt: serverTimestamp(),
      reversedBy: currentUser?.uid || '',
      reversedByName: currentUser?.email || '',
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'reverse_stock_adjustment',
      entityType: 'inventory',
      entityId: itemId,
      entityName: itemName,
      details: { 
        originalVariance: variance, 
        systemCount, 
        physicalCount,
        reason: adjustment.reason 
      },
    });

    return {
      success: true,
      message: 'Stock adjustment reversed successfully',
      newStock,
    };
  } catch (error) {
    console.error('Error reversing stock adjustment:', error);
    return { success: false, error: error.message };
  }
};
